import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { enGB } from 'date-fns/locale';
import * as dateFns from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import { GetCountryRevenue } from 'reactRedux/thunks/dashboardThunk/dashboardThunk';
import { formatCurrency } from 'utils/CommonFunction';
import { customStaticRanges } from 'utils/StaticData';

const RegionCard = ({ region }) => (
  <Box
    bg="gray.50"
    borderRadius="12px"
    p="16px"
    border="1px solid"
    borderColor="gray.100"
  >
    <Flex justify="space-between" align="center" mb="12px">
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {region.regionName}
      </Text>
      <Text fontSize="sm" fontWeight="600" color="gray.500">
        {region.currency}
      </Text>
    </Flex>

    <Flex direction="column" gap="6px">
      <Flex justify="space-between">
        <Text fontSize="sm" color="gray.500">
          Captured
        </Text>
        <Text fontSize="sm" fontWeight="500" color="gray.700">
          {formatCurrency(region.capturedAmount, region.currency)}
        </Text>
      </Flex>

      <Flex justify="space-between">
        <Text fontSize="sm" color="gray.500">
          Refund
        </Text>
        <Text fontSize="sm" fontWeight="500" color="red.500">
          -{formatCurrency(region.refundAmount, region.currency)}
        </Text>
      </Flex>

      <Flex justify="space-between">
        <Text fontSize="sm" color="gray.500">
          Stripe Fees
        </Text>
        <Text fontSize="sm" fontWeight="500" color="red.500">
          -{formatCurrency(region.stripeFees, region.currency)}
        </Text>
      </Flex>

      <Box borderTop="1px solid" borderColor="gray.200" pt="8px" mt="4px">
        <Flex justify="space-between" align="center">
          <Text fontSize="sm" fontWeight="700" color="gray.700">
            Net Revenue
          </Text>
          <Flex align="center" gap="6px">
            <Text fontSize="md" fontWeight="700" color="blue.600">
              {formatCurrency(region.netRevenue, region.currency)}
            </Text>
            {region.percentageChange !== null &&
            region.percentageChange !== undefined ? (
              <Text
                fontSize="xs"
                fontWeight="600"
                color={region.percentageChange >= 0 ? 'green.500' : 'red.500'}
              >
                ({region.percentageChange >= 0 ? '↑' : '↓'}{' '}
                {Math.abs(region.percentageChange).toFixed(1)}%)
              </Text>
            ) : (
              <Text fontSize="xs" color="gray.400">
                No prev data
              </Text>
            )}
          </Flex>
        </Flex>
      </Box>
    </Flex>
  </Box>
);

RegionCard.propTypes = {
  region: PropTypes.shape({
    regionId: PropTypes.number.isRequired,
    regionName: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    capturedAmount: PropTypes.number.isRequired,
    refundAmount: PropTypes.number.isRequired,
    stripeFees: PropTypes.number.isRequired,
    netRevenue: PropTypes.number.isRequired,
    percentageChange: PropTypes.number,
  }).isRequired,
};

export default function CountryRevenueOverview() {
  const dispatch = useDispatch();
  const { countryRevenueData, isCountryRevenueLoading } = useSelector(
    (state) => state.DashboardSlice,
  );

  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarFilterTitle, setCalendarFilterTitle] =
    useState('Last 30 days');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 30 days');
  const [dateRange] = useState([
    {
      startDate: dateFns.addDays(new Date(), -29),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(dateRange);

  const fetchData = useCallback(
    (range) => {
      const start = dateFns.format(range[0].startDate, 'yyyy/MM/dd');
      const end = dateFns.format(range[0].endDate, 'yyyy/MM/dd');
      dispatch(GetCountryRevenue({ start, end }));
    },
    [dispatch],
  );

  useEffect(() => {
    fetchData(dateRange);
  }, [fetchData, dateRange]);

  const handleSelect = useCallback((ranges) => {
    const matched = customStaticRanges.find(
      (r) =>
        r.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        r.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );
    setTempCalendarFilterTitle(matched?.label ?? 'Custom');
    setTempDateRange([ranges.selection]);
  }, []);

  const handleApply = useCallback(() => {
    setCalendarFilterTitle(tempCalendarFilterTitle);
    setAppliedDateRange(tempDateRange);
    setShowCalendar(false);
    fetchData(tempDateRange);
  }, [tempDateRange, tempCalendarFilterTitle, fetchData]);

  const handleClear = useCallback(() => {
    setTempDateRange(dateRange);
    setTempCalendarFilterTitle(calendarFilterTitle);
    setShowCalendar(false);
  }, [dateRange, calendarFilterTitle]);

  const isEmpty = !isCountryRevenueLoading && countryRevenueData.length === 0;

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb="20px"
        flexWrap="wrap"
        gap="12px"
      >
        <Box>
          <Text fontSize="lg" fontWeight="700" color="gray.700">
            Country-wise Net Revenue
          </Text>
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            {dateFns.format(appliedDateRange[0].startDate, 'dd-MM-yyyy')} -{' '}
            {dateFns.format(appliedDateRange[0].endDate, 'dd-MM-yyyy')}
          </Text>
        </Box>

        <Box position="relative">
          <Button
            py="10px"
            px="15"
            fontWeight="semibold"
            type="button"
            bg="white"
            border="1px solid"
            borderColor="reviveGray"
            color="#292D32"
            borderRadius="8px"
            onClick={() => setShowCalendar(!showCalendar)}
            justifyContent="space-between"
            isDisabled={isCountryRevenueLoading}
          >
            {calendarFilterTitle}{' '}
            <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
          </Button>

          {showCalendar && (
            <>
              <Box
                position="fixed"
                bg="rgb(0 0 0 / 40%)"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex="9"
                onClick={() => setShowCalendar(false)}
              />
              <Box
                p="32px"
                mt="8px"
                position="absolute"
                right="0"
                zIndex="10"
                bg="white"
                boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                borderRadius="20px"
                className="custom-date-picker"
              >
                <DateRangePicker
                  ranges={tempDateRange}
                  onChange={handleSelect}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  direction="horizontal"
                  locale={enGB}
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                  staticRanges={customStaticRanges}
                  inputRanges={[]}
                />
                <Flex justifyContent="right" gap="12px" marginTop="30px">
                  <Button
                    className="clear-btn"
                    border="1px solid"
                    borderColor="reviveGray"
                    borderRadius="10px"
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                  <Button
                    className="apply-btn"
                    bg="#6658fd"
                    border="1px solid"
                    borderColor="#6658fd"
                    color="white"
                    borderRadius="10px"
                    _hover={{ bg: 'transparent', color: '#6658fd' }}
                    onClick={handleApply}
                  >
                    Apply
                  </Button>
                </Flex>
              </Box>
            </>
          )}
        </Box>
      </Flex>

      {isCountryRevenueLoading && (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" color="blue.500" />
        </Flex>
      )}

      {isEmpty && (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">
            No revenue data for selected period.
          </Text>
        </Flex>
      )}

      {!isCountryRevenueLoading && countryRevenueData.length > 0 && (
        <>
          <Grid
            templateColumns={{
              base: 'repeat(1, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(4, 1fr)',
            }}
            gap="16px"
            mb="28px"
          >
            {countryRevenueData.map((region) => (
              <GridItem key={region.regionId}>
                <RegionCard region={region} />
              </GridItem>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}
